import {
  Component,
  OnInit,
  OnDestroy,
  signal,
  computed,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { Subject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';

import { MessageService, MenuItem } from 'primeng/api';
import { Toast } from 'primeng/toast';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { ChartModule } from 'primeng/chart';
import { MultiSelectModule } from 'primeng/multiselect';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';

import { SupplierDashboardService } from '../../services/supplier-dashboard.service';
import { NavigationContextService } from '../../../core/services/navigation-context.service';
import {
  DashboardFilters,
  DashboardFilterOption,
  DashboardGlobalFiltersResponse,
  DashboardSummaryResponse,
  DashboardContractActivityDto,
  DashboardContractActivitiesResponse,
} from '../../models/supplier-dashboard.model';

const AXA_COLORS = {
  blue:        '#00125b',
  brightBlue:  '#2563eb',
  green:       '#16a34a',
  amber:       '#d97706',
  orange:      '#ea580c',
  red:         '#dc2626',
  purple:      '#7c3aed',
  gray:        '#6b7280',
  darkGray:    '#374151',
  lightBlue:   '#3b82f6',
};

@Component({
  selector: 'app-suppliers-dashboard',
  standalone: true,
  providers: [MessageService],
  imports: [
    CommonModule,
    FormsModule,
    Toast,
    BreadcrumbModule,
    ButtonModule,
    ChartModule,
    MultiSelectModule,
    TableModule,
    TagModule,
    SkeletonModule,
    TooltipModule,
    DialogModule,
  ],
  templateUrl: './suppliers-dashboard.html',
  styleUrl: './suppliers-dashboard.scss',
})
export class SuppliersDashboardComponent implements OnInit, OnDestroy {
  private dashboardService = inject(SupplierDashboardService);
  private messageService    = inject(MessageService);
  private router            = inject(Router);
  private titleService      = inject(Title);
  private navigationContext = inject(NavigationContextService);
  private destroy$          = new Subject<void>();

  // ── Breadcrumb ─────────────────────────────────────────────
  breadcrumbItems = signal<MenuItem[]>([{ label: 'Dashboard Fornitori' }]);

  // ── Loading flags ─────────────────────────────────────────
  loadingFilters     = signal(false);
  loadingSummary     = signal(false);
  loadingActivities  = signal(false);

  // ── Global filter options (from API) ──────────────────────
  companyOptions     = signal<DashboardFilterOption[]>([]);
  referentOptions    = signal<string[]>([]);
  serviceTypeOptions = signal<DashboardFilterOption[]>([]);

  // ── Selected filters ──────────────────────────────────────
  selectedCompanies    = signal<string[]>([]);
  selectedReferents    = signal<string[]>([]);
  selectedServiceTypes = signal<string[]>([]);

  // Helper: current active filters object
  private get activeFilters(): DashboardFilters {
    return {
      companies:    this.selectedCompanies().length    ? this.selectedCompanies()    : undefined,
      referents:    this.selectedReferents().length    ? this.selectedReferents()    : undefined,
      serviceTypes: this.selectedServiceTypes().length ? this.selectedServiceTypes() : undefined,
    };
  }

  // ── Summary widget data ────────────────────────────────────
  summary = signal<DashboardSummaryResponse | null>(null);

  // Chart configs (built from summary)
  suppliersByCompanyChartData    = signal<any>(null);
  suppliersByCompanyChartOptions = signal<any>(null);
  contractsStatusChartData       = signal<any>(null);
  contractsStatusChartOptions    = signal<any>(null);
  spendingChartData              = signal<any>(null);
  spendingChartOptions           = signal<any>(null);

  // ── Company suppliers modal ────────────────────────────────
  companyModalVisible   = false;
  companyModalLabel     = signal('');
  companyModalSuppliers = signal<string[]>([]);

  // ── Activities widget ─────────────────────────────────────
  activities      = signal<DashboardContractActivityDto[]>([]);
  activitiesTotal = signal(0);
  activitiesPage  = signal(0);
  activitiesSize  = signal(10);

  // ── Lookup: company valueKey → valueDescription ────────────
  private companyLabelMap = computed<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    this.companyOptions().forEach((c) => {
      map[c.valueKey] = c.valueDescription;
    });
    return map;
  });

  // ── KPI status cards (computed from summary) ───────────────
  kpiCards = computed(() => {
    const s = this.summary()?.contractsStatus;
    if (!s) return [];
    return [
      { label: 'Attivi',        value: s.active,    styleClass: 'kpi-active',   statuses: ['ACTIVE']             },
      { label: 'In Rinnovo',    value: s.inRenewal,  styleClass: 'kpi-renewal',  statuses: ['IN_RENEWAL']         },
      { label: 'In Scadenza',   value: s.expiring,   styleClass: 'kpi-expiring', statuses: ['EXPIRING']           },
      { label: 'Scaduti',       value: s.expired,    styleClass: 'kpi-expired',  statuses: ['EXPIRED']            },
      { label: 'Bozze',         value: s.draft,      styleClass: 'kpi-draft',    statuses: ['DRAFT', 'GENERATED'] },
      { label: 'Cessati',       value: s.ceased,     styleClass: 'kpi-ceased',   statuses: ['CEASED']             },
    ];
  });

  // ──────────────────────────────────────────────────────────
  ngOnInit(): void {
    this.titleService.setTitle('Dashboard Fornitori - Portale AXA');
    this.loadGlobalFilters();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Data loading ───────────────────────────────────────────

  loadGlobalFilters(): void {
    this.loadingFilters.set(true);
    this.dashboardService.getGlobalFilters()
      .pipe(takeUntil(this.destroy$), finalize(() => this.loadingFilters.set(false)))
      .subscribe({
        next: (res) => {
          if (res?.status && res.data) {
            const data: DashboardGlobalFiltersResponse = res.data;
            this.companyOptions.set(data.companies ?? []);
            this.referentOptions.set(data.referents ?? []);
            this.serviceTypeOptions.set(data.serviceTypes ?? []);
          }
          this.loadDashboard();
        },
        error: () => {
          this.showError('Impossibile caricare i filtri globali.');
          this.loadDashboard();
        },
      });
  }

  loadDashboard(): void {
    this.loadSummary();
    this.loadActivities(0);
  }

  private loadSummary(): void {
    this.loadingSummary.set(true);
    this.dashboardService.getSummary({ ...this.activeFilters, topN: 10 })
      .pipe(takeUntil(this.destroy$), finalize(() => this.loadingSummary.set(false)))
      .subscribe({
        next: (res) => {
          if (res?.status && res.data) {
            this.summary.set(res.data);
            this.buildCharts(res.data);
          }
        },
        error: () => this.showError('Impossibile caricare i dati di riepilogo.'),
      });
  }

  private loadActivities(page: number): void {
    this.loadingActivities.set(true);
    this.activitiesPage.set(page);
    this.dashboardService.getContractActivities(this.activeFilters, page, this.activitiesSize())
      .pipe(takeUntil(this.destroy$), finalize(() => this.loadingActivities.set(false)))
      .subscribe({
        next: (res) => {
          if (res?.status && res.data) {
            const data: DashboardContractActivitiesResponse = res.data;
            this.activities.set(data.content ?? []);
            this.activitiesTotal.set(data.totalElements ?? 0);
          }
        },
        error: () => this.showError('Impossibile caricare le attività pendenti.'),
      });
  }

  // ── User interactions ──────────────────────────────────────

  onFiltersChange(): void {
    this.loadDashboard();
  }

  resetFilters(): void {
    this.selectedCompanies.set([]);
    this.selectedReferents.set([]);
    this.selectedServiceTypes.set([]);
    this.loadDashboard();
  }

  onActivitiesPage(event: { first: number; rows: number }): void {
    const page = Math.floor(event.first / event.rows);
    this.activitiesSize.set(event.rows);
    this.loadActivities(page);
  }

  navigateToContract(contractId: string): void {
    this.router.navigate([`/axa/${this.navigationContext.getDomainPrefix()}-contracts-registry`, contractId]);
  }

  filterContractsByStatus(statuses: string[]): void {
    this.router.navigate(['/axa', 'supplier-contracts-registry'], {
      queryParams: { status: statuses },
    });
  }

  navigateToSupplier(supplierId: number): void {
    this.router.navigate(['/axa/supplier', supplierId]);
  }

  onCompanyBarClick(event: any): void {
    const idx = event?.element?.index ?? event?.elements?.[0]?.index;
    if (idx === undefined || idx === null) return;
    const items = this.summary()?.suppliersByCompany ?? [];
    const item = items[idx];
    if (!item) return;
    this.companyModalLabel.set(this.getCompanyLabel(item.company));
    this.companyModalSuppliers.set(item.suppliers ?? []);
    this.companyModalVisible = true;
  }

  showAllCompaniesModal(): void {
    const items = this.summary()?.suppliersByCompany ?? [];
    const grouped = items.flatMap((i) =>
      (i.suppliers ?? []).map((s) => `${this.getCompanyLabel(i.company)}: ${s}`)
    );
    this.companyModalLabel.set('Tutti i fornitori per azienda');
    this.companyModalSuppliers.set(grouped);
    this.companyModalVisible = true;
  }

  // ── Helpers ────────────────────────────────────────────────

  getCompanyLabel(valueKey: string): string {
    return this.companyLabelMap()[valueKey] ?? valueKey;
  }

  getDaysRemaining(endDate: string | null): number | null {
    if (!endDate) return null;
    const diff = new Date(endDate).getTime() - Date.now();
    return Math.ceil(diff / 86_400_000);
  }

  getDaysRemainingClass(days: number | null): string {
    if (days === null) return '';
    if (days < 0)  return 'days-overdue';
    if (days <= 30) return 'days-critical';
    if (days <= 90) return 'days-warning';
    return 'days-ok';
  }

  getStatusSeverity(status: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    switch (status) {
      case 'ACTIVE':     return 'success';
      case 'IN_RENEWAL': return 'info';
      case 'EXPIRING':   return 'warn';
      case 'DRAFT':
      case 'FILLED':
      case 'GENERATED':  return 'secondary';
      case 'EXPIRED':    return 'danger';
      case 'CEASED':     return 'danger';
      default:           return 'secondary';
    }
  }

  getStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      ACTIVE:     'Attivo',
      IN_RENEWAL: 'In Rinnovo',
      EXPIRING:   'In Scadenza',
      DRAFT:      'Bozza',
      FILLED:     'Compilato',
      GENERATED:  'Generato',
      EXPIRED:    'Scaduto',
      CEASED:     'Cessato',
    };
    return labels[status] ?? status;
  }

  formatCurrency(value: number | null, currency = 'EUR'): string {
    if (value === null || value === undefined) return '—';
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  }

  // ── Chart builders ─────────────────────────────────────────

  private buildCharts(data: DashboardSummaryResponse): void {
    this.buildSuppliersByCompanyChart(data);
    this.buildContractsStatusChart(data);
    this.buildSpendingChart(data);
  }

  private buildSuppliersByCompanyChart(data: DashboardSummaryResponse): void {
    const palette = [
      '#1e3a8a', // navy blue  (es. AXA)
      '#60a5fa', // light blue (es. QUIXA)
      '#16a34a', // green      (es. AAI)
      '#a16207', // yellow-dark (es. AMAD) — WCAG 1.4.3: #eab308 was 1.9:1 on white; #a16207 is 4.9:1
      '#ea580c', // orange     (es. combinazioni 2 aziende)
      '#dc2626', // red        (es. combinazioni 2 aziende)
      '#9ca3af', // gray       (es. Multi-Company)
      '#7c3aed', // purple     (overflow)
    ];
    const items = data.suppliersByCompany ?? [];
    this.suppliersByCompanyChartData.set({
      labels: items.map((i) => this.getCompanyLabel(i.company)),
      datasets: [
        {
          label: 'Fornitori operativi',
          data: items.map((i) => i.suppliersCount),
          backgroundColor: items.map((_, idx) => palette[idx % palette.length]),
          borderRadius: 4,
          barThickness: 35,
          categoryPercentage: 0.7,
        },
      ],
    });
    this.suppliersByCompanyChartOptions.set({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx: any) => {
              const idx = ctx.dataIndex;
              const item = items[idx];
              const names = (item?.suppliers ?? []).slice(0, 5).join(', ');
              const more = (item?.suppliers?.length ?? 0) > 5
                ? ` (+${item.suppliers.length - 5} altri)` : '';
              return [`${ctx.parsed.y} fornitori`, names + more];
            },
          },
        },
      },
      scales: {
        y: { beginAtZero: true, ticks: { stepSize: 1 } },
        x: { ticks: { font: { size: 12 } } },
      },
    });
  }

  private buildContractsStatusChart(data: DashboardSummaryResponse): void {
    const s = data.contractsStatus;
    if (!s) return;
    this.contractsStatusChartData.set({
      labels: ['Attivi', 'In Rinnovo', 'In Scadenza', 'Scaduti', 'Bozze', 'Cessati'],
      datasets: [
        {
          data: [s.active, s.inRenewal, s.expiring, s.expired, s.draft, s.ceased],
          backgroundColor: [
            AXA_COLORS.green,
            AXA_COLORS.brightBlue,
            AXA_COLORS.amber,
            AXA_COLORS.red,
            AXA_COLORS.gray,
            AXA_COLORS.darkGray,
          ],
          borderWidth: 2,
          borderColor: '#ffffff',
        },
      ],
    });
    this.contractsStatusChartOptions.set({
      responsive: true,
      maintainAspectRatio: true,
      aspectRatio: 1.2,
      cutout: '65%',
      plugins: {
        legend: {
          position: 'bottom' as const,
          labels: { padding: 20, font: { size: 14 } },
        },
        tooltip: {
          callbacks: {
            label: (ctx: any) =>
              ` ${ctx.label}: ${ctx.parsed} (${
                s.total > 0
                  ? ((ctx.parsed / s.total) * 100).toFixed(1)
                  : 0
              }%)`,
          },
        },
      },
    });
  }

  private buildSpendingChart(data: DashboardSummaryResponse): void {
    const items = data.spendingBySupplier ?? [];
    const currencyFormatter = new Intl.NumberFormat('it-IT', {
      style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0,
    });
    this.spendingChartData.set({
      labels: items.map((i) => i.supplierName),
      datasets: [
        {
          label: 'Valore contratti',
          data: items.map((i) => i.totalContractValue),
          backgroundColor: items.map((_, idx) => {
            const palette = [
              AXA_COLORS.blue, AXA_COLORS.brightBlue, AXA_COLORS.lightBlue,
              AXA_COLORS.purple, AXA_COLORS.orange, AXA_COLORS.amber,
              AXA_COLORS.green,
            ];
            return palette[idx % palette.length];
          }),
          borderRadius: 4,
        },
      ],
    });
    this.spendingChartOptions.set({
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: 'y',
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx: any) => ` ${currencyFormatter.format(ctx.parsed.x)}`,
          },
        },
      },
      scales: {
        x: {
          beginAtZero: true,
          ticks: {
            callback: (v: number) => {
              if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M €`;
              if (v >= 1_000)    return `${(v / 1_000).toFixed(0)}K €`;
              return `${v} €`;
            },
          },
        },
        y: { ticks: { font: { size: 12 } } },
      },
    });
  }

  getExtraSupplierNames(suppliers: { supplierName: string }[], visibleCount: number): string {
    return suppliers.slice(visibleCount).map((s) => s.supplierName).join(', ');
  }

  private showError(detail: string): void {
    this.messageService.add({ severity: 'error', summary: 'Errore', detail, life: 5000 });
  }
}
